import type { LocaleCatalogShape } from '@/shared/i18n'
import type { guildAdminEn } from './en.ts'

/**
 * Russian plural-form agreement for the masculine inanimate nouns
 * `билд` (build) and `игрок` (player). Russian has three count forms:
 *
 *   - **one**: nominative singular — `1 билд`, `21 билд`, `1 игрок`.
 *   - **few** (2-4): genitive singular — `2 билда`, `22 билда`,
 *     `2 игрока`, `24 игрока`.
 *   - **many** (0, 5-20, 25-30, …): genitive plural — `5 билдов`,
 *     `11 билдов`, `12 билдов`, `13 билдов`, `14 билдов`,
 *     `25 билдов`, `5 игроков`, `11 игроков`.
 *
 * The 11-14 exception: tens whose last two digits are 11-14 always
 * take the **many** form, never **few**, even though 2-4 would suggest
 * `few`. Numbers are normalised so callers can pass either a `number`
 * or a numeric `string` (e.g. `21` vs `"21"`), and so that negative
 * or non-finite inputs fall back to the **many** form.
 */
function russianMasculineForm(
  count: number,
  one: string,
  few: string,
  many: string,
): string {
  const absolute = Math.abs(count)
  if (!Number.isFinite(absolute)) return many
  const mod100 = absolute % 100
  const mod10 = absolute % 10
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

function pluralizeBuild(count: number): string {
  return russianMasculineForm(count, 'билд', 'билда', 'билдов')
}

function pluralizePlayer(count: number): string {
  return russianMasculineForm(count, 'игрок', 'игрока', 'игроков')
}

export const guildAdminRu: LocaleCatalogShape<typeof guildAdminEn> = {
  management: {
    title: 'Настройки гильдии',
    mobileDescription: 'Настройте автоматизацию Discord, доступ участников и общую библиотеку планировщика.',
    desktopDescription: 'Управляйте автоматизацией Discord, доступом и общими данными планировщика в одном месте.',
    sectionsLabel: 'Разделы настроек гильдии',
    discord: 'Настройки Discord',
    discordDescription: 'Настройте автоматическую публикацию рейдов, язык и роли для управления гильдией и доступа к ней.',
    members: 'Доступ участников',
    membersDescription: 'Рассматривайте запросы доступа и синхронизируйте список офицеров с Discord.',
    library: 'Теги и билды',
    libraryDescription: 'Управляйте общими метками и библиотекой билдов планировщика гильдии.',
  },
  language: {
    title: 'Язык гильдии',
    description: 'Задаёт автоматический язык для участников гильдии и сообщений, отправляемых на этот сервер Discord.',
    label: 'Язык',
    english: 'Английский',
    russian: 'Русский',
    save: 'Сохранить язык гильдии',
    saving: 'Сохранение…',
    saved: 'Язык гильдии сохранён.',
    chooseGuild: 'Выберите гильдию, прежде чем менять её язык.',
    failed: 'Не удалось сохранить язык гильдии.',
  },
  accessRole: {
    title: 'Роль доступа к гильдии',
    description: 'Если роль задана, гильдию видят только участники Discord с этой ролью. При сохранении проверяется, может ли бот её назначать.',
    roleId: 'ID роли Discord',
    placeholder: 'Оставьте пустым, чтобы разрешить доступ всем участникам',
    publicStatus: 'Все текущие участники сервера Discord могут видеть эту гильдию.',
    restrictedStatus: 'Участники без этой роли могут запросить доступ у владельца гильдии.',
    roleName: (name: string) => `Роль Discord: ${name}`,
    save: 'Сохранить роль доступа',
    saving: 'Сохранение…',
    invalid: 'Введите корректный ID роли Discord или оставьте поле пустым, чтобы разрешить доступ всем участникам.',
    chooseGuild: 'Выберите гильдию, прежде чем менять роль доступа.',
    failed: 'Не удалось сохранить роль доступа.',
  },
  officerRole: {
    title: 'Роль офицера',
    description: 'Участники с этой ролью Discord получают права офицера после синхронизации состава ботом.',
    roleId: 'ID роли Discord',
    placeholder: 'Оставьте пустым, чтобы отключить доступ офицеров по роли',
    disabledStatus: 'Сейчас ни одна роль Discord не даёт права офицера.',
    enabledStatus: 'Бот применит эту роль при следующей синхронизации состава и при последующих изменениях ролей.',
    roleName: (name: string) => `Роль Discord: ${name}`,
    save: 'Сохранить роль офицера',
    saving: 'Сохранение…',
    invalid: 'Введите корректный ID роли Discord или оставьте поле пустым, чтобы отключить доступ по роли.',
    chooseGuild: 'Выберите гильдию, прежде чем менять роль офицера.',
    failed: 'Не удалось сохранить роль офицера.',
  },
  publication: {
    title: 'Публикация рейдов',
    description: 'Публикуйте запланированные рейды в одном текстовом канале Discord, пока они входят в заданный период.',
    channelId: 'ID текстового канала Discord',
    channelPlaceholder: 'Оставьте пустым, чтобы отключить автоматическую публикацию',
    daysAhead: 'За сколько дней публиковать рейды',
    completedMessageRetentionHours: 'На сколько часов сохранять сообщения о завершённых рейдах',
    completedMessageRetentionHint: 'Карточки завершённых рейдов удаляются после окончания рейда; по умолчанию через 48 часов.',
    disabledStatus: 'Автоматическая публикация отключена.',
    enabledStatus: (days: string) => `Будут опубликованы рейды, запланированные на ближайшие ${days} дн.`,
    channelName: (name: string) => `Канал Discord: #${name}`,
    save: 'Сохранить настройки публикации',
    saving: 'Сохранение…',
    verifyFailed: (reason: string) => `Не удалось проверить этот канал Discord: ${reason}`,
    invalidChannel: 'Введите корректный ID канала Discord или оставьте поле пустым, чтобы отключить публикацию.',
    invalidDays: 'Введите целое число от 1 до 30 дней.',
    invalidCompletedMessageRetentionHours: 'Введите целое число от 1 до 720 часов.',
    chooseGuild: 'Выберите гильдию, прежде чем менять настройки публикации.',
    failed: 'Не удалось сохранить настройки публикации.',
  },
  notifications: {
    title: 'Голосовые уведомления о лесе',
    description: 'Воспроизводите уведомления о лесе одновременно в Discord и TeamSpeak 6.',
    fleetAvailability: (configured: number, available: number, channels: number) => `Discord: Настроено бот-аккаунтов: ${configured} · доступно в этой гильдии: ${available} · до ${channels} каналов одновременно`,
    accountStatus: (index: number, role: 'coordinator' | 'worker', ready: boolean, guildPresent: boolean, requiresOauthCodeGrant: boolean) => {
      const label = `${role === 'coordinator' ? 'Координатор' : 'Рабочий бот'} #${index}`
      if (!guildPresent && requiresOauthCodeGrant) {
        return `${label}: прямое приглашение недоступно, потому что включён Require OAuth2 Code Grant. Отключите его в настройках Bot приложения Discord; статус обновится автоматически.`
      }
      if (!ready) return `${label}: шлюз Discord недоступен`
      return guildPresent ? `${label}: готов в этой гильдии` : `${label}: подключён, но не добавлен в эту гильдию`
    },
    inviteBot: (index: number, role: 'coordinator' | 'worker') => `Добавить ${role === 'coordinator' ? 'координатора' : 'рабочего бота'} #${index} на сервер ↗`,
    capacityWarning: (saved: number, draft: number, concurrent: number) => `Discord: ${saved === draft ? `Сохранено каналов: ${saved}.` : `Сохранено каналов: ${saved}; в этой форме указано: ${draft}. Сохраните настройки уведомлений, чтобы записать изменения.`} Каждое уведомление смогут получить не более ${concurrent}, пока не будут добавлены бот-аккаунты.`,
    enabled: 'Включить голосовые уведомления',
    automatic: 'Автоматически запускать для запланированных рейдов',
    interval: 'Интервал леса (секунды)',
    offsets: 'Время до уведомлений (секунды)',
    offsetsPlaceholder: '30, 10',
    mode: 'Режим воспроизведения',
    audio: 'Аудио',
    soundboard: 'Саундбар Discord',
    teamspeakChannels: 'Каналы TeamSpeak 6',
    teamspeakSelected: (count: number, limit: number) => `Выбрано: ${count} / ${limit}`,
    teamspeakRefresh: 'Обновить каналы',
    teamspeakRetry: 'Повторить загрузку каналов',
    teamspeakLoading: 'Загрузка каналов TeamSpeak…',
    teamspeakEmpty: 'Нет доступных каналов TeamSpeak.',
    teamspeakLoadFailed: 'Не удалось загрузить каналы TeamSpeak.',
    teamspeakMissingChannel: (id: string) => `Сохранённый канал #${id} (недоступен)`,
    teamspeakHint: 'Выберите каналы для уведомлений. Один бот передаёт звук во все выбранные каналы TeamSpeak одновременно с уведомлениями Discord.',
    teamspeakUnavailable: 'TeamSpeak не настроен для этой гильдии. Попросите администратора сервера подключить звукового бота. Сохранённые каналы остаются выбранными.',
    teamspeakAudioHint: 'Выбор саундбара относится к Discord. В TeamSpeak воспроизводится настроенный звук уведомления.',
    voiceChannels: 'ID голосовых каналов Discord',
    voiceChannelId: (position: number) => `ID голосового канала Discord ${position}`,
    addVoiceChannel: 'Добавить голосовой канал',
    removeVoiceChannel: (position: number) => `Удалить голосовой канал ${position}`,
    voiceChannelName: (name: string) => `Голосовой канал Discord: ${name}`,
    foreignVoiceChannelInviteHelp: 'Этот голосовой канал принадлежит другому серверу Discord. Добавьте на него все отсутствующие аккаунты wwm-notify перед проверкой или сохранением этого назначения.',
    inviteBotToForeignVoiceGuild: (index: number, role: 'coordinator' | 'worker') => `Добавить ${role === 'coordinator' ? 'координатора' : 'рабочего бота'} #${index} на этот сервер ↗`,
    foreignVoiceChannelCodeGrant: (index: number, role: 'coordinator' | 'worker') => `Прямое приглашение для ${role === 'coordinator' ? 'координатора' : 'рабочего бота'} #${index} недоступно, потому что включён Require OAuth2 Code Grant. Отключите его в настройках Bot этого приложения Discord; приглашение обновится автоматически.`,
    validation: {
      voice_channels_required: 'Добавьте голосовой канал Discord или выберите канал TeamSpeak.',
      voice_channels_limit: 'Укажите не более 16 голосовых каналов.',
      voice_channel_invalid: 'ID голосовых каналов Discord должны содержать от 17 до 20 цифр.',
      voice_channel_duplicate: 'Каждый ID голосового канала можно указать только один раз.',
      teamspeak_channels_limit: 'Укажите не более 16 каналов TeamSpeak.',
      teamspeak_channel_invalid: 'ID TeamSpeak должны быть целыми положительными числами до 18446744073709551615 без начальных нулей.',
      teamspeak_channel_duplicate: 'Каждый ID канала TeamSpeak можно указать только один раз.',
      offsets_required: 'Укажите хотя бы одно время до уведомления.',
      offset_invalid: 'Время до уведомления должно быть целым числом секунд от 0 до 3600.',
      offset_duplicate: 'Каждое время до уведомления можно указать только один раз.',
    },
    soundId: 'ID звука саундбара',
    sourceGuildId: 'ID сервера-источника саундбара',
    save: 'Сохранить настройки уведомлений',
    saving: 'Сохранение…',
    test: 'Проверить уведомление',
    testing: 'Проверка…',
    tested: (report: string) => `Проверка уведомления: ${report}`,
    loading: 'Загрузка настроек уведомлений…',
    retry: 'Повторить загрузку',
    saved: 'Настройки уведомлений сохранены.',
    invalidInterval: 'Введите целое число от 60 до 3600 секунд.',
    invalidSoundId: 'Введите корректный ID звука саундбара Discord.',
    invalidSourceGuildId: 'Введите корректный ID сервера-источника Discord.',
    chooseGuild: 'Выберите гильдию, прежде чем менять настройки уведомлений.',
  },
  notificationTimers: {
    title: 'Таймеры уведомлений',
    description: 'Актуальные таймеры гильдии, ротация леса и ручное управление wwm-notify в одном месте.',
    setupDescription: 'Добавьте бота-координатора wwm-notify на этот сервер, прежде чем управлять таймерами уведомлений.',
    setupAdd: 'Добавьте бота wwm-notify на этот сервер Discord.',
    setupReturn: 'Вернитесь сюда после подтверждения приглашения Discord — элементы управления таймерами появятся автоматически.',
    inviteBot: 'Добавить бота wwm-notify на этот сервер ↗',
    inviteUnavailable: 'Прямая ссылка-приглашение недоступна. Отключите Require OAuth2 Code Grant для приложения wwm-notify; статус обновится автоматически.',
    liveSync: 'Синхронизация',
    liveConnecting: 'Подключение',
    liveIssue: 'Ошибка синхронизации',
    gameTimer: 'Игровой таймер (необязательно)',
    gameTimerPlaceholder: 'Пусто, -3:20 или 23:25',
    gameTimerHelp: 'Игра длится 30 минут. Используйте -ММ:СС до старта или ММ:СС до конца игры.',
    gameTimerOptionalHelp: 'Оставьте поле пустым, чтобы начать игру сейчас.',
    modifyTimerHelp: 'Оставьте игровой таймер пустым, чтобы сохранить текущее расписание.',
    fixedDuration: 'Игра длится 30 минут',
    quickGuide: 'Краткая памятка по таймеру',
    blankValue: 'Пусто',
    blankExample: 'Игра начинается сейчас.',
    beforeStartExample: '-3:20 означает, что до начала игры осталось 3 минуты 20 секунд.',
    runningExample: '23:25 означает, что до конца игры осталось 23 минуты 25 секунд.',
    name: 'Название таймера (необязательно)',
    namePlaceholder: 'Ротация леса',
    nameOptionalHelp: 'Таймер без названия отображается как «Лес».',
    unnamed: 'Лес',
    addTitle: 'Добавить таймер',
    add: 'Добавить таймер',
    adding: 'Добавление…',
    listTitle: 'Текущие таймеры гильдии',
    listLabel: 'Текущие таймеры уведомлений гильдии',
    loading: 'Загрузка таймеров уведомлений…',
    retry: 'Повторить загрузку таймеров',
    waitingForLiveSync: 'Подписка восстановится автоматически.',
    empty: 'Для этой гильдии нет активных или запланированных таймеров.',
    countdownHelp: 'Отсчёты обновляются локально каждую секунду по данным подписки.',
    nextNotification: 'Уведомление',
    nextForest: 'Следующий лес',
    gameEndsIn: 'До конца игры',
    notScheduled: '—',
    countdown: (clock: string) => `через ${clock}`,
    scheduleRange: (start: string, end: string) => `${start} → ${end}`,
    sourceLabel: 'Источник',
    statusLabel: 'Статус',
    actionsLabel: 'Действия с таймером',
    startsAt: 'Начало',
    endsAt: 'Окончание',
    duration: 'Длительность',
    durationMinutes: (minutes: number) => `${minutes} мин`,
    source: {
      manual: 'Вручную',
      external: 'Планировщик',
    },
    status: {
      scheduled: 'Запланирован',
      running: 'Запущен',
      finished: 'Завершён',
      stopped: 'Остановлен',
    },
    readOnly: 'Управляется планировщиком',
    plannerSaved: 'Таймер планировщика обновлён. wwm-notify синхронизирует его.',
    plannerStopped: 'Таймер планировщика остановлен. Опубликуйте рейд снова в Календаре, чтобы перезапустить его.',
    modify: 'Изменить',
    start: 'Запустить',
    restart: 'Перезапустить',
    stop: 'Остановить',
    stopping: 'Остановка…',
    remove: 'Удалить',
    removing: 'Удаление…',
    modifyAria: (name: string) => `Изменить таймер ${name}`,
    startAria: (name: string, restart: boolean) => `${restart ? 'Перезапустить' : 'Запустить'} таймер ${name}`,
    stopAria: (name: string) => `Остановить таймер ${name}`,
    removeAria: (name: string) => `Удалить таймер ${name}`,
    editTitle: (name: string) => `Изменить ${name}`,
    startTitle: (name: string) => `${name}: запуск или перезапуск`,
    saveChanges: 'Сохранить изменения',
    starting: 'Запуск…',
    saving: 'Сохранение…',
    cancel: 'Отмена',
    channelTitle: 'Канал статуса активных таймеров',
    channelDescription: 'wwm-notify поддерживает в этом текстовом канале одно сообщение со списком активных таймеров. Если активных таймеров нет, оно показывает «no timers».',
    channelId: 'ID текстового канала Discord',
    channelPlaceholder: 'Оставьте пустым, чтобы отключить сообщение статуса',
    channelName: (name: string) => `Текстовый канал Discord: #${name}`,
    channelDisabled: 'Канал статуса таймеров не настроен.',
    channelMessage: (messageId: string) => `ID сообщения статуса: ${messageId}`,
    channelConnected: 'Сообщение подключено',
    saveChannel: 'Сохранить канал статуса',
    savingChannel: 'Сохранение…',
    clearChannel: 'Очистить канал',
    validation: {
      game_timer_invalid: 'Введите ММ:СС с необязательным знаком + или - и секундами от 00 до 59.',
      game_timer_finished: '0:00 означает, что игра уже закончилась.',
      game_timer_exceeds_duration: 'Оставшееся время не может быть больше 30:00.',
      channelInvalid: 'Введите корректный ID канала Discord из 17–20 цифр.',
    },
    saved: 'Таймер уведомлений обновлён.',
    removed: 'Таймер уведомлений удалён.',
    channelSaved: 'Канал статуса таймеров обновлён.',
    chooseGuild: 'Выберите гильдию, прежде чем менять таймеры уведомлений.',
    failed: 'Не удалось обновить таймеры уведомлений.',
  },
  requests: {
    title: 'Запросы доступа',
    description: 'При одобрении запроса бот назначит настроенную роль доступа к гильдии.',
    loading: 'Загрузка запросов доступа…',
    configureFirst: 'Сначала настройте роль доступа.',
    empty: 'Нет ожидающих запросов доступа.',
    listLabel: 'Ожидающие запросы доступа',
    requested: (date: string) => `Запрошено: ${date}`,
    grantLabel: (name: string) => `Назначить ${name} настроенную роль доступа`,
    granting: 'Назначение…',
    grant: 'Выдать роль доступа',
    failed: 'Не удалось одобрить этот запрос доступа.',
  },
  onboarding: {
    accessTitle: 'Требуется доступ к гильдии',
    accessDescription: 'Ваша учётная запись Discord состоит в этих гильдиях, но для просмотра планировщика требуется роль доступа.',
    pending: 'Запрос доступа ожидает рассмотрения',
    roleRequired: 'Для просмотра требуется роль Discord',
    requestPending: 'Запрос ожидает',
    requesting: 'Отправка…',
    request: 'Запросить доступ',
    requestFailed: 'Не удалось запросить доступ к этой гильдии.',
    loginTitle: 'Войдите через Discord',
    loginDescription: 'Войдите через Discord, чтобы увидеть свои гильдии и добавить бота на сервер.',
    login: 'Войти через Discord',
    emptyTitle: 'Гильдий пока нет',
    emptyDescription: 'Вы не состоите ни на одном сервере Discord, где установлен этот бот. Добавьте бота на свой сервер, чтобы начать планировать рейды.',
    addBotFromSelector: 'Используйте «Добавить бота на сервер Discord» в переключателе гильдий выше.',
    refresh: 'После добавления бота обновите страницу — сервер появится в списке выше.',
  },
  roster: {
    title: 'Управление офицерами',
    ownerOnly: 'Изменять эти настройки может только владелец гильдии в Discord.',
    loading: 'Загрузка состава…',
    empty: 'Состав ещё не синхронизирован. Попросите бота выполнить /sync_members в Discord.',
    owner: 'Владелец',
    officer: 'Офицер',
    ownerAria: (name: string) => `Статус офицера для ${name}. Владелец всегда является офицером; доступно только для чтения.`,
    toggleAria: (name: string) => `Переключить статус офицера для ${name}`,
    ownerTitle: 'Владелец всегда является офицером; эта строка доступна только для чтения.',
    toggleTitle: 'Переключить статус офицера',
    failed: 'Не удалось изменить статус офицера.',
  },
  builds: {
    title: 'Билды гильдии',
    description: 'Управляйте общей библиотекой билдов гильдии и назначениями игрокам.',
    counts: (builds: number, players: number) =>
          `${builds} ${pluralizeBuild(builds)} · ${players} ${pluralizePlayer(players)}`,
    viewLabel: 'Режим просмотра билдов гильдии',
    library: 'Библиотека',
    assignments: 'Назначения',
    listLabel: 'Билды гильдии',
    buildNumber: (number: number) => `Билд №${number}`,
    assignedToNone: 'Назначен: —',
    assignedTo: (names: string) => `Назначен: ${names}`,
    editFirst: 'Изменить первое',
    editSecond: 'Изменить второе',
    remove: 'Удалить',
    empty: 'В гильдии пока нет билдов. Создайте первый.',
    create: '+ Создать билд гильдии',
    buildCount: (count: number) => `${count} ${pluralizeBuild(count)}`,
    unassignAria: (number: number, name: string) => `Отменить назначение билда №${number} для ${name}`,
    noneAssigned: 'Билды гильдии не назначены.',
    assignAria: (name: string) => `Назначить билд гильдии для ${name}`,
    assign: '+ Назначить',
    availableAria: (name: string) => `Доступные билды для ${name}`,
    noAvailable: 'Нет доступных неназначенных билдов.',
    noPlayers: 'На доске пока нет игроков.',
    noWeapons: 'Оружие не выбрано',
    chooseFirst: 'Выберите первое оружие билда гильдии',
    chooseSecond: 'Выберите второе оружие билда гильдии',
    pickerOpen: 'Окно выбора уже открыто',
    createTitle: 'Создать новый билд гильдии',
    removeConfirm: (number: number, summary: string) => `Удалить билд гильдии №${number} (${summary})? Он будет снят со всех игроков.`,
    selectPlayersAria: (number: number) => `Выбрать игроков для билда гильдии №${number}`,
    playersAria: (number: number) => `Игроки билда гильдии №${number}`,
    assignPlayers: 'Назначить игроков',
    editPlayers: 'Изменить игроков',
    assigned: 'Назначен',
    assignOne: 'Назначить',
  },
}
